import { create } from 'zustand'
import axios from 'axios'
import { cleanParamsFilter } from 'Utils/cleanParamsFilter'

import { CRUDBaseStore } from './Base/CRUD'

export const API_SLUG = 'Users'

const getApiErrorMessage = async (err: any, fallback = 'Request failed') => {
  const data = err?.response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text())
      return parsed?.message?.toString() ?? fallback
    } catch {
      return fallback
    }
  }
  return err?.response?.data?.message?.toString() ?? fallback
}

interface UserStore extends Omit<CRUDBaseStore, 'edit' | 'delete'> {
  getOne: (id: string) => Promise<{ success: boolean; data: any }>
  edit: (id: string, data: any) => Promise<{ success: boolean; data: any }>
  delete: (id: string) => Promise<{ success: boolean; data: any }>
  uploadSignature: (id: string, file: File) => Promise<{ success: boolean; data: any }>
  getSignature: (id: string) => Promise<{ success: boolean; data: any }>
  deleteSignature: (id: string) => Promise<{ success: boolean; data: any }>
}

export const INITIAL_STATE = {
  username: '',
  employeeCode: '',
  fullName: '',
  position: '',
}

export const useUserStore = create<UserStore>(set => ({
  data: [],
  selected: INITIAL_STATE,
  loading: false,
  error: undefined,
  setLoading: (loading: boolean) => {
    set({ loading })
  },
  setError: (error: any) => {
    set({ error })
  },
  setSelected: (selected: any) => {
    set({ selected })
  },
  getAll: async (filter: any) => {
    set({ loading: true })
    const finaleFilter = cleanParamsFilter(filter)

    return axios
      .get(`/${API_SLUG}`, { params: finaleFilter })
      .then(res => {
        set({ data: res.data.data })
        set({ loading: false })

        return { success: true, data: res.data.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  getOne: async id => {
    set({ loading: true })

    return axios
      .get(`/${API_SLUG}/${id}`)
      .then(res => {
        const user = res.data?.data ?? res.data
        set({ selected: user })
        set({ loading: false })

        return { success: true, data: user }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  create: async (data: any) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (key === 'categories') {
          data[key].forEach((item: any) => {
            formData.append('categoryId[]', item)
          })
        } else {
          formData.append(key, data[key])
        }
      }
    })

    return axios
      .post(`/${API_SLUG}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  edit: async (id, data) => {
    set({ loading: true })

    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (key === 'categories') {
          data[key].forEach((item: any) => {
            formData.append('categoryId[]', item)
          })
        } else {
          formData.append(key, data[key])
        }
      }
    })

    return axios
      .put(`/${API_SLUG}/${id}`, formData)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  delete: async id => {
    set({ loading: true })
    return axios
      .delete(`/${API_SLUG}/${id}`)
      .then(res => {
        set({ loading: false })
        return { success: true, data: res.data }
      })
      .catch(err => {
        set({ loading: false })
        set({ error: err?.response?.data?.message?.toString() })
        return { success: false, data: err?.response?.data?.message?.toString() }
      })
  },
  uploadSignature: async (id, file) => {
    const formData = new FormData()
    formData.append('signatureFile', file)
    return axios
      .post(`/${API_SLUG}/${id}/signature`, formData)
      .then(res => ({ success: true, data: res.data }))
      .catch(async err => ({
        success: false,
        data: await getApiErrorMessage(err, 'อัปโหลดลายเซ็นไม่สำเร็จ'),
      }))
  },
  getSignature: async id => {
    return axios
      .get(`/${API_SLUG}/${id}/signature`, { responseType: 'blob' })
      .then(res => ({
        success: true,
        data: URL.createObjectURL(res.data as Blob),
      }))
      .catch(async err => ({
        success: false,
        data: await getApiErrorMessage(err, 'โหลดไฟล์ลายเซ็นไม่สำเร็จ'),
      }))
  },
  deleteSignature: async id => {
    return axios
      .delete(`/${API_SLUG}/${id}/signature`)
      .then(res => ({ success: true, data: res.data }))
      .catch(async err => ({
        success: false,
        data: await getApiErrorMessage(err, 'ลบลายเซ็นไม่สำเร็จ'),
      }))
  },
  clearState: () => {
    set({
      data: [],
      loading: false,
      error: undefined,
      selected: INITIAL_STATE,
    })
  },
}))
